export const IMAGE_GENERATION_CONTRACT_VERSION = 'image-generation/v1'

const IMAGE_GENERATION_MODES = new Set(['generate', 'reference', 'edit'])

function text(value) {
  return typeof value === 'string' ? value.trim() : ''
}

function cloneValue(value) {
  if (Array.isArray(value)) return value.map(cloneValue)
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, cloneValue(item)]))
  }
  return value
}

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value
  Object.values(value).forEach(deepFreeze)
  return Object.freeze(value)
}

function requireText(value, field) {
  const normalized = text(value)
  if (!normalized) throw new TypeError(`image_intent.${field} is required`)
  return normalized
}

function normalizedReferences(values) {
  if (!Array.isArray(values)) throw new TypeError('image_intent.reference_asset_ids must be an array')
  const result = values.map((value) => requireText(value, 'reference_asset_ids'))
  if (new Set(result).size !== result.length) {
    throw new TypeError('image_intent.reference_asset_ids contains duplicates')
  }
  return result
}

function normalizedExtensions(values) {
  if (!Array.isArray(values)) throw new TypeError('image_intent.prompt_extensions must be an array')
  return values.map((value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      throw new TypeError('image_intent.prompt_extensions must contain objects')
    }
    return {
      kind: requireText(value.kind, 'prompt_extensions.kind'),
      content: requireText(value.content, 'prompt_extensions.content')
    }
  })
}

function payloadReferences(source) {
  if (Array.isArray(source?.reference_asset_ids) && source.reference_asset_ids.length > 0) {
    return source.reference_asset_ids.slice()
  }
  return Array.isArray(source?.reference_image_ids)
    ? source.reference_image_ids.slice()
    : []
}

/** Build the versioned page-to-kernel intent while retaining legacy top-level fields. */
export function createImageGenerationIntent({
  appCode,
  mediaType = 'image',
  metadata = {},
  mode,
  prompt,
  promptExtensions = [],
  referenceAssetIds = [],
  technicalParams = {}
} = {}) {
  const normalizedMode = requireText(mode, 'mode').toLowerCase()
  if (!IMAGE_GENERATION_MODES.has(normalizedMode)) {
    throw new TypeError(`image_intent.mode is unsupported: ${normalizedMode}`)
  }
  const normalizedMediaType = requireText(mediaType, 'media_type').toLowerCase()
  if (normalizedMediaType !== 'image') {
    throw new TypeError('image_intent.media_type must be image')
  }
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    throw new TypeError('image_intent.metadata must be an object')
  }
  if (!technicalParams || typeof technicalParams !== 'object' || Array.isArray(technicalParams)) {
    throw new TypeError('image_intent.technical_params must be an object')
  }
  return deepFreeze({
    contract_version: IMAGE_GENERATION_CONTRACT_VERSION,
    mode: normalizedMode,
    app_code: requireText(appCode, 'app_code'),
    media_type: normalizedMediaType,
    prompt: requireText(prompt, 'prompt'),
    prompt_extensions: normalizedExtensions(promptExtensions),
    reference_asset_ids: normalizedReferences(referenceAssetIds),
    technical_params: cloneValue(technicalParams),
    metadata: cloneValue(metadata)
  })
}

/** Build the shared intent from an already assembled pipeline payload. */
export function createImageGenerationIntentFromPayload(payload = {}, { mode, promptExtensions } = {}) {
  const source = payload && typeof payload === 'object' ? payload : {}
  const referenceAssetIds = payloadReferences(source)
  const inferredMode = text(source.app_code).toLowerCase() === 'ai_edit'
    ? 'edit'
    : (referenceAssetIds.length ? 'reference' : 'generate')
  return createImageGenerationIntent({
    appCode: source.app_code,
    mediaType: source.media_type || 'image',
    metadata: source.metadata || {},
    mode: mode || inferredMode,
    prompt: source.prompt,
    promptExtensions: typeof promptExtensions === 'undefined'
      ? (source.prompt_extensions || [])
      : promptExtensions,
    referenceAssetIds,
    technicalParams: source.technical_params || {}
  })
}
